import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MetaCreativeKind } from '../../../../../../models/meta-template/preview-interfaces/meta-preview-data.interface';

/** How a placement frames the creative. */
export type MetaCreativeSlot = 'feed' | 'story' | 'fullscreen' | 'fit' | 'cover';

/**
 * The ad creative as every Meta placement draws it. The template takes one image
 * (1080x1080 or 1080x1920, single image ad) or one video (1440x1440 or 1440x1800,
 * video ad). A video plays muted and looped, the way feeds autoplay it.
 *
 * The placements frame the creative the way Meta does:
 *   - `feed`: a square slot, or 4:5 for a vertical upload (Meta's tallest feed
 *     crop), filled edge to edge.
 *   - `story`: fills its 9:16 parent. A 9:16 upload fills it; anything shorter
 *     (square, 4:5) is shown whole over a blurred copy of itself.
 *   - `fullscreen`: fills its parent, letterboxed on black whatever the
 *     orientation (in-stream video players).
 *   - `fit`: fills its parent, shown whole on the page's own background
 *     (Audience Network native).
 *   - `cover`: fills a box its placement sizes, cropped to it (thumbnails,
 *     grid tiles, Marketplace cards).
 *
 * The orientation is only known once the image or the video's metadata has loaded,
 * so the slot starts square and switches when it has.
 */
@Component({
  selector: 'app-meta-creative',
  imports: [CommonModule],
  templateUrl: './meta-creative.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meta-creative.component.scss'
})
export class MetaCreativeComponent implements OnChanges {
  @Input() src = '';
  @Input() slot: MetaCreativeSlot = 'feed';
  /** Whether `src` is an image or a video. */
  @Input() kind: MetaCreativeKind = 'image';

  /** Taller than square: the feed slot goes 4:5. */
  portrait = false;
  /** Close to 9:16: the story slot is filled edge to edge instead of letterboxed over a blur. */
  tall = false;
  /** Set when <video> cannot play `src` (a GIF uploaded to a video ad); it is then drawn as an image. */
  videoFailed = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['src'] || changes['kind']) {
      this.videoFailed = false;
    }
  }

  get asVideo(): boolean {
    return this.kind === 'video' && !this.videoFailed;
  }

  onLoad(image: HTMLImageElement): void {
    this.measure(image.naturalWidth, image.naturalHeight);
  }

  onVideoMetadata(video: HTMLVideoElement): void {
    this.measure(video.videoWidth, video.videoHeight);
  }

  onVideoError(): void {
    this.videoFailed = true;
  }

  private measure(width: number, height: number): void {
    this.portrait = height > width * 1.05;
    this.tall = height >= width * 1.7;
  }
}
